import { useMutation } from 'react-query';
import { useCallback } from 'react';
import { getEmployeeUseCases } from '@personnel/composition';
import { useApiErrorHandler } from './useApiErrorHandler';

/**
 * Hook for generating an employee PIN
 *
 * @returns {Object} Hook result with generatePin function and loading state
 * @returns {Function} returns.generatePin - Function that returns the generated PIN string
 * @returns {boolean} returns.isGeneratingPin - Whether PIN generation is in progress
 * @returns {Error|null} returns.error - Error if generation failed
 *
 * @example
 * const { generatePin, isGeneratingPin } = useGenerateEmployeePin();
 * const pin = await generatePin(); // Returns PIN string directly
 */
export const useGenerateEmployeePin = () => {
  const { handleError } = useApiErrorHandler();
  const { generatePin: generatePinUseCase } = getEmployeeUseCases();

  const mutation = useMutation({
    mutationFn: () => generatePinUseCase.execute(),
    onError: (error) => handleError(error),
  });

  /**
   * Generates a PIN and returns the PIN string directly
   * Extracts the pin from PinGenerateResponseDto for convenience
   */
  const generatePin = useCallback(async (): Promise<string> => {
    const response = await mutation.mutateAsync();
    return response.pin;
  }, [mutation]);

  return {
    generatePin,
    isGeneratingPin: mutation.isLoading,
    error: mutation.error,
  };
};
